<template>
    <div
        v-click-outside="closeClassificationDropdown"
        class="af-classification-selector afu-block afu-box-border focus:afu-outline-none afu-appearance-none afu-w-full afu-transition afu-duration-150 afu-ease-in-out afu-text-black afu-placeholder-custom afu-text-base afu-border-inputBorder focus:afu-border-inputBorder afu-bg-white afu-h-full"
        :class="{ 'afu-border afu-border-solid afu-border-l afu-border-r afu-border-t afu-border-b': labelInside == 'true'}"
        @keydown.space.exact.prevent="toggleVisibility"
    >
        <div class="classification-selector-wrapper">
            <div
                class="classification-selector afu-flex afu-items-center afu-justify-between afu-cursor-pointer"
                :class="{ 'afu-px-3 afu-py-4 md:afu-p-sb-input': labelInside == 'true', 'afu-h-10 af-input afu-border-solid': labelInside == 'false' }"
                @click="toggleClassificationDropdown"
                @keyup.enter="toggleVisibility"
            >
                <div
                    class="cls-text pr-3"
                    :class="{ 'afu-py-2 afu-px-3': labelInside == 'false' }"
                >
                    <div
                        class="af-text--5xl afu-text-secondary cls-label"
                        v-if="labelInside == 'true'"
                    >
                        <span class="afu-font-bold afu-tracking-widest afu-uppercase">{{ $t('searchbar.classifications_label') }}</span>
                    </div>
                    <div
                        class="afu-text-xs md:afu-text-6xl afu-font-normal"
                        :class="{ 'md:afu-text-7xl': labelInside == 'false' }"
                        v-html="getClassificationDisplayLabel"
                    />
                </div>
                <div class="classification-tools afu-flex afu-items-center afu-justify-end">
                    <div
                        v-if="isClassificationSelected"
                        @click="clearClassification"
                        class="classifition-tool-icon afu-mr-6 afu-w-3 afu-text-neutral-dark"
                    >
                        <svg
                            class="afu-h-3 afu-w-3"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 8 8"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-width="1.5"
                                d="M1 1l6 6m0-6L1 7"
                            />
                        </svg>
                    </div>
                    <div class="classifition-tool-icon afu-w-3 afu-mr-2">
                        <FontAwesomeIcon
                            v-if="isClassificationOpen"
                            :icon="['fas', 'caret-up']"
                        />
                        <FontAwesomeIcon
                            v-else
                            :icon="['fas', 'caret-down']"
                        />
                    </div>
                </div>
            </div>
            <div
                v-if="isClassificationOpen"
                class="cls-selector-dropdown afu-py-2 afu-px-3 afu-shadow-lg afu-z-100 afu-max-h-md afu-overflow-y-scroll"
            >
                <div class="cls-dropdown-header afu-text-secondary afu-text-5xl">
                    {{ $t('label.classification_select') }}
                </div>
                <ul class="cls-list">
                    <li
                        v-for="(classification, classificationIndex) in classificationOptions"
                        :key="classificationIndex"
                        class="cls-item afu-mb-2"
                        :class="classification.is_selected ? 'selected' : ''"
                    >
                        <div
                            class="afu-mb-2 afu-mr-3"
                            @click="openClassification(classification)"
                        >
                            <span class="cls-item-checkbox afu-mr-2 afu-text-secondary">
                                <FontAwesomeIcon
                                    v-if="classification.is_open"
                                    :icon="['fas', 'check-square']"
                                />
                                <FontAwesomeIcon
                                    v-else
                                    :icon="['far', 'square']"
                                />
                            </span>
                            <span
                                class="cls-item-label"
                                :class="classification.is_open ? 'selected' : ''"
                            >
                                <span
                                    class="cls-item-label-text"
                                >
                                    {{ classification.value }}
                                </span>
                                <span
                                    class="cls-item-count afu-float-right"
                                    v-if="!classification.is_open && showTaxonomyCount"
                                >
                                    {{ classification.count | numeral }}
                                </span>
                            </span>
                        </div>
                        <div
                            v-show="classification.is_open"
                            class="cls-sub-dropdown afu-py-2 afu-px-3"
                        >
                            <div class="cls-select-all-cls afu-mb-2">
                                <ul class="cls-sub-list">
                                    <li
                                        class="cls-item afu-mb-2"
                                        :class="classification.is_selected ? 'selected' : ''"
                                    >
                                        <div @click="selectClassification(classification)">
                                            <span class="cls-item-checkbox afu-mr-2 afu-text-secondary">
                                                <FontAwesomeIcon
                                                    v-if="classification.is_selected"
                                                    :icon="['fas', 'check-square']"
                                                />
                                                <FontAwesomeIcon
                                                    v-else
                                                    :icon="['far', 'square']"
                                                />
                                            </span>
                                            <span
                                                class="cls-item-label"
                                                :class="classification.is_selected ? 'selected' : ''"
                                            >
                                                All
                                                <span
                                                    class="cls-item-label-text"
                                                >
                                                    {{ classification.value }}
                                                </span>
                                                <span
                                                    class="cls-item-count afu-float-right"
                                                    v-if="showTaxonomyCount"
                                                >
                                                    {{ classification.count | numeral }}
                                                </span>
                                            </span>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                            <div class="cls-sub-cls-list afu-pt-3">
                                <div class="cls-sub-cls-list-header afu-mb-3">
                                    {{ $t('label.sub_classification_select') }}
                                </div>
                                <ul class="cls-sub-list">
                                    <li
                                        v-for="(sub, index1) in classification.subclassifications"
                                        :key="index1"
                                        @click="selectSubClassification(classification, sub)"
                                        class="cls-item afu-mb-2"
                                        :class="sub.is_selected ? 'selected' : ''"
                                    >
                                        <span class="cls-item-checkbox afu-mr-2 afu-text-secondary">
                                            <FontAwesomeIcon
                                                v-if="sub.is_selected"
                                                :icon="['fas', 'check-square']"
                                            />
                                            <FontAwesomeIcon
                                                v-else
                                                :icon="['far', 'square']"
                                            />
                                        </span>
                                        <span
                                            class="cls-item-label"
                                            :class="sub.is_selected ? 'selected' : ''"
                                        >
                                            <span
                                                class="cls-item-label-text"
                                            >
                                                {{ sub.value }}
                                            </span>
                                            <span
                                                class="cls-item-count afu-float-right"
                                                v-if="showTaxonomyCount"
                                            >
                                                {{ sub.count | numeral }}
                                            </span>
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
    import '@/plugins/v-click-outside';
    import { mapState, mapActions } from 'vuex';
    import numeral from 'numeral';

    export default {
        name: 'ClassificationSelecter',

        filters: {
            numeral(value) {
                if (!value) {
                    return;
                }
                return numeral(value).format('0,0');
            },
        },

        data() {
            return {
                classifications: [],
                classificationSelectedCount: 0,
                subClassificationSelectedCount: 0,
                selectedClassifications: [],
                isClassificationOpen: false,
            };
        },

        props: {
            initClassifications: {
                type: Array,
                default: () => [],
            },

            closeDropdown: {
                type: Boolean,
                default: false,
            },
            labelInside: {
                type: String,
                default: 'true',
            },
        },

        created() {
            this.$_getClassifications().then(response => {
                if (response.data) {
                    const classifications = response.data.map(c => {
                        c.is_selected = false;
                        c.is_open = false;
                        c.subclassifications = c.subclassifications.map(s => {
                            s.is_selected = false;
                            return s;
                        });
                        return c;
                    });
                    this.classifications = [...classifications];
                    this.classificationOptions = [...classifications];
                    this.preloadClassifications();
                }
            });
        },

        computed: {
            ...mapState({
                Classifications(state) {
                    return state.search.searchPayload.search_results?.aggregation_all?.classification || [];
                },
                subClassifications(state) {
                    return state.search.searchPayload.search_results?.aggregation_all?.subclassification || [];
                },
                
                showTaxonomyCount() {
                    return (window.afConfig?.hide_taxonomy_count === 'Yes') ? false : true;
                },
            }),

            getClassificationDisplayLabel() {
                const count =
                    this.classificationSelectedCount +
                    this.subClassificationSelectedCount;

                if (count >= 1) {
                    if (this.subClassificationSelectedCount > 0) {
                        return `in <span class="afu-font-bold">${count} classification / sub-classification</span>`;
                    } else {
                        return `in <span class="afu-font-bold">${count} classification</span>`;
                    }
                } else {
                    return this.$t('searchbar.classification_placeholder');
                }
            },

            isClassificationSelected() {
                return (
                    this.classificationSelectedCount > 0 ||
                    this.subClassificationSelectedCount > 0
                );
            },
        },

        watch: {
            isClassificationOpen() {
                if (this.isClassificationOpen === false) {
                    this.$emit(
                        'updateClassification',
                        this.selectedClassifications,
                    );
                }
            },

            initClassifications() {
                this.preloadClassifications();
            },

            closeDropdown(v) {
                if (v === true && this.isClassificationOpen === true) {
                    this.isClassificationOpen = false;
                }
            },
        },

        methods: {
            ...mapActions('classification', {
                $_getClassifications: 'getSeekerClassifications',
            }),

            toggleClassificationDropdown() {
                this.isClassificationOpen = !this.isClassificationOpen;
            },

            closeClassificationDropdown() {
                this.isClassificationOpen = false;
            },

            openClassification(classification) {
                classification.is_open = !classification.is_open;
                this.selectClassification(classification);
            },

            preloadClassifications() {
                this.classificationSelectedCount = 0;
                this.subClassificationSelectedCount = 0;

                const classifications = [...this.classifications];
                classifications.forEach(cla => {
                    cla.is_selected = false;
                    cla.is_open = false;
                    const subClassifications = cla.subclassifications.map(s => {
                        s.is_selected = false;
                        return s;
                    });

                    subClassifications.forEach(sub => {
                        sub.count = '0';
                        const aggrSubCla = this.subClassifications;
                        if (aggrSubCla) {
                            aggrSubCla.forEach((agg_scla) => {
                                if (parseInt(agg_scla.id) === parseInt(sub.id) && parseInt(agg_scla.cla_id) === parseInt(cla.id)) {
                                    sub.count = agg_scla.count;
                                }
                            });
                        }
                    });

                    this.initClassifications.forEach(s => {
                        if (parseInt(cla.id) === parseInt(s.id)) {
                            cla.is_open = true;
                            if (s.subclassifications.length) {
                                cla.is_selected = false;

                                subClassifications.forEach(sub => {
                                    sub.is_selected = false;
                                    s.subclassifications.forEach(ssub => {
                                        if (
                                            parseInt(sub.id) === parseInt(ssub.id)
                                        ) {
                                            sub.is_selected = true;
                                            this.subClassificationSelectedCount += 1;
                                        }
                                    });
                                });
                            } else {
                                cla.is_selected = true;
                                this.classificationSelectedCount += 1;
                            }
                        }
                    });

                    cla.count = '0';
                    const aggrCla = this.Classifications;
                    if (aggrCla) {
                        aggrCla.forEach((agg_cla) => {
                            if (parseInt(agg_cla.id) === parseInt(cla.id)) {
                                cla.count = agg_cla.count;
                            }
                        });
                    }
                    cla.subclassifications = subClassifications;
                });

                this.classificationOptions = [...classifications];
                this.selectedClassifications = [...this.initClassifications];
            },

            selectClassification(classification) {
                classification.is_selected = !classification.is_selected;

                classification.subclassifications.forEach(s => {
                    if (s.is_selected) {
                        s.is_selected = false;
                        this.subClassificationSelectedCount -= 1;
                    }
                });

                if (classification.is_selected) {
                    this.classificationSelectedCount += 1;
                } else {
                    classification.is_open = false;
                    this.classificationSelectedCount -= 1;
                }

                this.updateClassification(classification, null);
            },

            selectSubClassification(classification, subclassification) {
                if (classification.is_selected) {
                    classification.is_selected = false;
                    this.classificationSelectedCount -= 1;
                }

                subclassification.is_selected = !subclassification.is_selected;

                let selectedNone = true;
                let selected = 0;
                classification.subclassifications.forEach(c => {
                    if (c.is_selected === true) {
                        selectedNone = false;
                        selected += 1;
                    }
                });

                if (subclassification.is_selected) {
                    this.subClassificationSelectedCount += 1;
                } else {
                    this.subClassificationSelectedCount -= 1;
                }

                if (
                    selectedNone ||
                    selected === classification.subclassifications.length
                ) {
                    this.selectClassification(classification);
                } else {
                    this.updateClassification(classification, subclassification);
                }
            },

            clearClassification() {
                this.classifications.map(c => {
                    c.is_selected = false;
                    c.is_open = false;
                    c.subclassifications = c.subclassifications.map(s => {
                        s.is_selected = false;
                        return s;
                    });
                    return c;
                });
                this.isClassificationOpen = false;
                this.classificationSelectedCount = 0;
                this.selectedClassifications = [];
                this.$emit('updateClassification', this.selectedClassifications);
            },

            updateClassification(classification, subclassification) {
                let newClassification = true;

                this.selectedClassifications.forEach((c, index) => {
                    if (c.id === classification.id) {
                        newClassification = false;
                        if (subclassification === null) {
                            if (classification.is_selected) {
                                newClassification = true;
                            }
                            this.selectedClassifications.splice(index, 1);
                        } else {
                            let newSubClassification = true;
                            c.subclassifications.forEach((s, index2) => {
                                if (s.id === subclassification.id) {
                                    newSubClassification = false;
                                    c.subclassifications.splice(index2, 1);
                                    if (c.subclassifications.length === 0) {
                                        this.selectedClassifications.splice(
                                            index,
                                            1,
                                        );
                                    }
                                }
                            });

                            if (newSubClassification) {
                                c.subclassifications.push({
                                    id: subclassification.id,
                                    value: subclassification.value,
                                    slug: subclassification.slug,
                                });
                            }
                        }
                    }
                });

                if (newClassification) {
                    const sub = [];
                    if (subclassification) {
                        sub.push({
                            id: subclassification.id,
                            value: subclassification.value,
                            slug: subclassification.slug,
                        });
                    }

                    this.selectedClassifications.push({
                        id: classification.id,
                        value: classification.value,
                        slug: classification.slug,
                        subclassifications: sub,
                    });
                }

                this.$emit('updateClassification', this.selectedClassifications);
            },
        },
    };
</script>

<style lang="scss" scoped>
    .cls-label {
        margin-bottom: 1.5px;
    }

    .af-classification-selector {
        position: relative;

        .cls-selector-dropdown {
            position: absolute;
            left: 0;
            right: 0;
            background: #fff;
            top: 115%;
        }
    }

    .classification-selector-wrapper {
        .classification-selector {
            .cls-sub-label {
                span {
                    font-weight: bold;
                }
            }
        }

        .cls-selector-dropdown {
            max-height: 500px;

            .cls-dropdown-header {
                margin: 0 0 20px;
            }

            ul {
                padding: 0;
                margin: 0;
                list-style: none;
            }

            .cls-list {
                .cls-sub-dropdown {
                    background: #f9f9f9;

                    .cls-sub-list {
                        padding: 0;
                        margin: 0;
                        list-style: none;
                    }

                    .cls-sub-cls-list {
                        border-top: 1px solid #e5e6e2;

                        .cls-sub-cls-list-header {
                            font-size: 12px;
                            color: #aeaeaa;
                        }
                    }
                }

                .cls-item {
                    .cls-item-label {
                        font-size: 14px;
                        line-height: 21px;
                        font-weight: 300;

                        &.selected {
                            font-weight: bold;
                        }
                    }
                }
            }
        }
    }
</style>
